import { normalizeCompareText } from "./compareDiscrepancies";
import { formatSuggestCostUsd } from "./functions";

const PENDING_PHASES = new Set([
    "enviando",
    "aguardando_resposta",
    "await_reply",
    "avaliando",
]);

function shortModelLabel(model) {
    if (!model) return "?";
    if (model.label) return model.label;
    const s = String(model.id || "").replace(/^openrouter:/, "");
    const parts = s.split("/");
    return parts[parts.length - 1] || s || "?";
}

function clipText(text, max = 60) {
    const s = normalizeCompareText(text);
    if (!s) return "—";
    return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}

function formatLatencySeconds(latencyMs) {
    if (latencyMs == null || !Number.isFinite(Number(latencyMs))) return "—";
    const s = Number(latencyMs) / 1000;
    if (s < 10) return `${s.toFixed(1)}s`;
    return `${Math.round(s)}s`;
}

function formatCost(row) {
    const c = row?.usage?.cost_usd ?? row?.cost_usd;
    return formatSuggestCostUsd(c) ?? "—";
}

function formatTokens(row) {
    const u = row?.usage || {};
    if (u.prompt_tokens == null && u.completion_tokens == null) return "—";
    return `${u.prompt_tokens ?? "—"} / ${u.completion_tokens ?? "—"}`;
}

function turnOrderFromSuite(suiteSnapshot) {
    const cases = Array.isArray(suiteSnapshot?.cases) ? suiteSnapshot.cases : [];
    const flow = cases.find((c) => c?.kind === "flow") || cases[0];
    const turns = Array.isArray(flow?.turns) ? flow.turns : [];
    return turns.map((t, i) => ({
        turnId: String(t.id || `t${i + 1}`),
        input: t.mensagem || "",
        index: i + 1,
    }));
}

function resultFor(results, slot, turnId) {
    return (results || []).find(
        (r) => r.slot === slot && String(r.turnId) === String(turnId)
    );
}

function slotIsPendingTurn(slotProgress, turnIndex) {
    if (!slotProgress) return false;
    if (slotProgress.status === "completed" || slotProgress.status === "failed") {
        return false;
    }
    const phase = String(slotProgress.phase || "");
    const idx = Number(slotProgress.turnIndex) || 0;
    return idx === turnIndex && PENDING_PHASES.has(phase);
}

/**
 * Monta blocos da tabela comparativa (um por turno), com células por modelo.
 * Inclui turnos pending a partir de progress.slots + suite_snapshot.
 */
export function buildCompareTableTurns({
    results = [],
    models = [],
    progress = null,
    suiteSnapshot = null,
} = {}) {
    const orderedModels = [...models].sort((a, b) => a.slot - b.slot);
    const suiteTurns = turnOrderFromSuite(suiteSnapshot);
    const fromResults = [
        ...new Set((results || []).map((r) => String(r.turnId)).filter(Boolean)),
    ];
    const turnIds =
        suiteTurns.length > 0
            ? suiteTurns.map((t) => t.turnId)
            : fromResults.sort((a, b) => a.localeCompare(b));

    return turnIds.map((turnId, i) => {
        const suiteTurn = suiteTurns.find((t) => t.turnId === turnId);
        const turnIndex = suiteTurn?.index ?? i + 1;
        const inputFromSuite = suiteTurn?.input || "";
        const progressSlots = Array.isArray(progress?.slots) ? progress.slots : [];

        const columns = orderedModels.map((model) => {
            const row = resultFor(results, model.slot, turnId);
            const slotProgress = progressSlots.find((s) => s.slot === model.slot);
            const pending = !row && slotIsPendingTurn(slotProgress, turnIndex);
            const input =
                row?.input ||
                (pending
                    ? slotProgress?.message || inputFromSuite
                    : inputFromSuite) ||
                "";

            return {
                slot: model.slot,
                label: shortModelLabel(model),
                pending,
                phase: slotProgress?.phase || null,
                input,
                output: row ? row.output || "" : pending ? null : "",
                pass: row ? row.pass : null,
                intent: row?.intent ?? null,
                handoff_now: row?.handoff_now ?? null,
                latencyLabel: row ? formatLatencySeconds(row.latencyMs) : "—",
                costLabel: row ? formatCost(row) : "—",
                tokensLabel: row ? formatTokens(row) : "—",
                reason: row?.reason || null,
            };
        });

        const inputs = columns.map((c) => normalizeCompareText(c.input)).filter(Boolean);
        const sharedInput = inputs.length > 0 && new Set(inputs).size === 1 ? inputs[0] : null;

        return {
            turnId,
            turnIndex,
            sharedInput,
            columns,
        };
    });
}

/** Marca quais campos divergem entre colunas (para highlight). */
export function divergentFieldsForTurn(turnBlock) {
    const cols = (turnBlock?.columns || []).filter((c) => !c.pending && c.pass != null);
    if (cols.length < 2) {
        return new Set();
    }
    const fields = new Set();
    const check = (key, pick) => {
        const vals = new Set(cols.map((c) => pick(c)));
        if (vals.size > 1) fields.add(key);
    };
    check("pass", (c) => (c.pass === true ? "p" : c.pass === false ? "f" : "u"));
    check("intent", (c) => String(c.intent ?? ""));
    check("handoff", (c) => String(c.handoff_now ?? ""));
    check("output", (c) => normalizeCompareText(c.output));
    check("latency", (c) => c.latencyLabel);
    check("cost", (c) => c.costLabel);
    return fields;
}

export { shortModelLabel, clipText, formatLatencySeconds };
